/* ============================================================
   AdminCategoriesPanel — 3d4c488f: shop taxonomy management
   (two-level categories: roots + subcategories). Compact CRUD:
   inline add/edit form, activate toggle, delete when empty.
   Light theme, iOS-minimal per DESIGN-SYSTEM.md.
   ============================================================ */

import { useCallback, useEffect, useState } from 'react';
import { api } from '../api/client';
import type { ProductCategoryInput, ProductCategoryNode } from '../types/category';

interface FormState {
  id: string | null;
  name: string;
  slug: string;
  parentId: string;
  position: number;
  isActive: boolean;
}

const EMPTY: FormState = { id: null, name: '', slug: '', parentId: '', position: 0, isActive: true };

export function AdminCategoriesPanel() {
  const [tree, setTree] = useState<ProductCategoryNode[]>([]);
  const [form, setForm] = useState<FormState>(EMPTY);
  const [formError, setFormError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      setTree(await api.getAdminCategories());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Не удалось загрузить категории');
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  const save = async () => {
    setBusy(true);
    setFormError(null);
    const dto: ProductCategoryInput = {
      name: form.name.trim(),
      slug: form.slug.trim() || null,
      parentId: form.parentId || null,
      position: Number(form.position) || 0,
      isActive: form.isActive,
    };
    try {
      if (form.id) await api.updateCategory(form.id, dto);
      else await api.createCategory(dto);
      setForm(EMPTY);
      await load();
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Не удалось сохранить');
    } finally {
      setBusy(false);
    }
  };

  const toggleActive = async (node: ProductCategoryNode) => {
    setBusy(true);
    setError(null);
    try {
      await api.updateCategory(node.id, {
        name: node.name,
        slug: node.slug,
        parentId: node.parentId,
        position: node.position,
        isActive: !node.isActive,
      });
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Не удалось изменить');
    } finally {
      setBusy(false);
    }
  };

  const remove = async (node: ProductCategoryNode) => {
    setBusy(true);
    setError(null);
    try {
      await api.deleteCategory(node.id);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Не удалось удалить');
    } finally {
      setBusy(false);
    }
  };

  const startEdit = (node: ProductCategoryNode) => {
    setForm({
      id: node.id,
      name: node.name,
      slug: node.slug,
      parentId: node.parentId ?? '',
      position: node.position,
      isActive: node.isActive,
    });
    setFormError(null);
  };

  const fieldStyle: React.CSSProperties = {
    height: 40,
    padding: '0 var(--space-md)',
    borderRadius: 'var(--radius-md)',
    border: '1px solid var(--color-separator)',
    background: 'var(--color-surface)',
    fontSize: 'var(--fs-body)',
  };

  const roots = tree;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-md)' }}>
      {error && <p style={{ color: 'var(--color-danger)', margin: 0 }}>{error}</p>}

      {/* add / edit form */}
      <div className="card" style={{ padding: 'var(--space-md)', display: 'grid', gap: 'var(--space-sm)', gridTemplateColumns: '2fr 2fr 1fr auto auto', alignItems: 'center' }}>
        <input style={fieldStyle} placeholder={form.id ? 'Название' : 'Новая категория…'} value={form.name}
               onChange={(e) => setForm({ ...form, name: e.target.value })} />
        <select style={fieldStyle} value={form.parentId} onChange={(e) => setForm({ ...form, parentId: e.target.value })}>
          <option value="">Корневая</option>
          {roots.filter((r) => r.id !== form.id).map((r) => (
            <option key={r.id} value={r.id}>в «{r.name}»</option>
          ))}
        </select>
        <input style={fieldStyle} type="number" min={0} placeholder="№" value={form.position}
               onChange={(e) => setForm({ ...form, position: Number(e.target.value) || 0 })} />
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 'var(--fs-caption)', color: 'var(--color-text-secondary)', cursor: 'pointer' }}>
          <input type="checkbox" checked={form.isActive} onChange={(e) => setForm({ ...form, isActive: e.target.checked })} />
          активна
        </label>
        <div style={{ display: 'flex', gap: 'var(--space-xs)' }}>
          <button className="btn btn-primary" style={{ height: 40 }} disabled={busy || !form.name.trim()} onClick={() => void save()}>
            {form.id ? 'Сохранить' : 'Добавить'}
          </button>
          {form.id && (
            <button className="btn btn-secondary" style={{ height: 40 }} disabled={busy} onClick={() => setForm(EMPTY)}>
              Отмена
            </button>
          )}
        </div>
        <input style={{ ...fieldStyle, gridColumn: '1 / -1' }} placeholder="slug (латиница; пусто — сгенерировать из названия)"
               value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} />
        {formError && <p style={{ color: 'var(--color-danger)', margin: 0, gridColumn: '1 / -1' }}>{formError}</p>}
      </div>

      {/* tree list */}
      <div className="card" style={{ padding: 0 }}>
        {roots.length === 0 ? (
          <p style={{ padding: 'var(--space-md)', color: 'var(--color-text-secondary)', margin: 0 }}>
            Категорий пока нет — добавьте первую корневую категорию выше
          </p>
        ) : (
          roots.map((root) => (
            <div key={root.id} style={{ borderBottom: '1px solid var(--color-separator)' }}>
              <CategoryRow node={root} busy={busy} onEdit={() => startEdit(root)} onToggle={() => void toggleActive(root)} onRemove={() => void remove(root)} />
              {root.children.map((sub) => (
                <CategoryRow key={sub.id} node={sub} indent busy={busy} onEdit={() => startEdit(sub)} onToggle={() => void toggleActive(sub)} onRemove={() => void remove(sub)} />
              ))}
            </div>
          ))
        )}
      </div>
    </div>
  );
}

function CategoryRow({
  node, indent = false, busy, onEdit, onToggle, onRemove,
}: {
  node: ProductCategoryNode;
  indent?: boolean;
  busy: boolean;
  onEdit: () => void;
  onToggle: () => void;
  onRemove: () => void;
}) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-sm)', padding: '10px 16px', paddingLeft: indent ? 40 : 16 }}>
      <span style={{ flex: 1, fontSize: 'var(--fs-body)', fontWeight: indent ? 400 : 600, color: node.isActive ? 'var(--color-text-primary)' : 'var(--color-text-tertiary)' }}>
        {indent ? '↳ ' : ''}{node.name}
        <span style={{ fontSize: 'var(--fs-caption)', color: 'var(--color-text-secondary)' }}> · /{node.slug} · {node.productCount} тов.</span>
      </span>
      <button className="btn btn-tertiary" style={{ height: 32, padding: '4px 12px' }} disabled={busy} onClick={onToggle}>
        {node.isActive ? 'Скрыть' : 'Включить'}
      </button>
      <button className="btn btn-tertiary" style={{ height: 32, padding: '4px 12px' }} disabled={busy} onClick={onEdit}>
        Изменить
      </button>
      <button className="btn btn-tertiary" style={{ height: 32, padding: '4px 12px', color: 'var(--color-danger)' }} disabled={busy} onClick={onRemove}>
        Удалить
      </button>
    </div>
  );
}
